import React, { useEffect, useState } from 'react';
import './WireDesk.css';

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_ACCENTS = {
  weather:        '#7dd3fc',
  seismic:        '#fb7185',
  aviation:       '#a78bfa',
  geopolitics:    '#fcd34d',
  space:          '#d946ef',
  infrastructure: '#6ee7b7',
  local:          '#f472b6',
};

const IMPACT_RING = {
  critical: '#fb7185',
  high:     '#fcd34d',
  moderate: '#7dd3fc',
  low:      '#5d5e96',
};

function relativeTime(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const ms = Date.now() - t;
  if (ms < 0) return 'now';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default function WireDesk({ compact = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async (refresh = false) => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/api/wire/topics?max_topics=7${refresh ? '&refresh=true' : ''}`);
      if (!r.ok) throw new Error(`http ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = setInterval(() => load(false), 180000);
    return () => clearInterval(id);
  }, []);

  return (
    <section className={`wire-desk ${compact ? 'compact' : ''}`} data-testid="wire-desk">
      <header className="wire-desk-header">
        <div className="wire-desk-title">
          <span className="wire-pulse" />
          <span className="wire-name">WIRE</span>
          <span className="wire-sub">News desk · signal ingestion</span>
        </div>
        <div className="wire-desk-meta">
          {data && (
            <>
              <span title="High-signal topics returned">{data.topics?.length || 0} topics</span>
              <span>·</span>
              <span title="Items fresh / total polled">{data.items_high_signal}/{data.items_total}</span>
              <span>·</span>
              <span title="Sources OK / polled">{data.sources_ok}/{data.sources_polled} feeds</span>
              <span>·</span>
              <span title="Latest poll">{relativeTime(data.ingested_at_utc)}</span>
            </>
          )}
          <button
            className="wire-refresh"
            onClick={() => load(true)}
            disabled={busy}
            data-testid="wire-refresh"
          >
            {busy ? '...' : 'refresh'}
          </button>
        </div>
      </header>

      {err && <div className="wire-err">wire fetch failed: {err}</div>}

      <div className="wire-list" data-testid="wire-topics">
        {data?.topics?.map((t, idx) => {
          const accent = CAT_ACCENTS[t.category] || '#a78bfa';
          const ring = IMPACT_RING[t.impact_level] || '#5d5e96';
          return (
            <article
              key={`${t.timestamp_utc}-${idx}`}
              className="wire-topic"
              style={{ '--cat': accent, '--ring': ring }}
              data-testid={`wire-topic-${idx}`}
            >
              <div className="wire-topic-row1">
                <span className="wire-cat">{t.category}</span>
                <span className="wire-impact" style={{ color: ring }}>{t.impact_level}</span>
                <span className="wire-conf">{t.confidence}% conf</span>
                <span className="wire-score">score {t.score}</span>
                <span className="wire-time">{relativeTime(t.timestamp_utc)}</span>
              </div>
              <h3 className="wire-title">{t.title}</h3>
              {t.summary && <p className="wire-summary">{t.summary}</p>}
              <div className="wire-foot">
                {(t.matched_keywords || []).slice(0, 4).map((k) => (
                  <span key={k} className="wire-kw">{k}</span>
                ))}
                <span className="wire-spacer" />
                {(t.source_names || []).slice(0, 4).map((s) => (
                  <span key={s} className="wire-src">{s}</span>
                ))}
                {t.sources?.[0] && (
                  <a href={t.sources[0]} target="_blank" rel="noreferrer" className="wire-link">
                    open ›
                  </a>
                )}
              </div>
            </article>
          );
        })}
        {!data?.topics?.length && !busy && !err && (
          <div className="wire-empty">No high-signal topics in the last 12 hours. Check back in 3 minutes.</div>
        )}
      </div>
    </section>
  );
}
